import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { useRegister } from '@/api/hooks'
import { ApiError } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { fr } from '@/i18n/fr'

function fieldError(error: unknown, field: string): string | undefined {
  if (!(error instanceof ApiError)) return undefined
  const body = error.body
  if (typeof body !== 'object' || body === null || !(field in body)) return undefined
  const value = (body as Record<string, unknown>)[field]
  return Array.isArray(value) ? String(value[0]) : String(value)
}

export function RegisterPage() {
  const [searchParams] = useSearchParams()
  const [inviteCode, setInviteCode] = useState(searchParams.get('invite') ?? '')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const register = useRegister()
  const navigate = useNavigate()

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    register.mutate(
      { invite_code: inviteCode, email, password, display_name: displayName },
      { onSuccess: () => void navigate('/', { replace: true }) },
    )
  }

  const nonFieldError = register.isError ? fieldError(register.error, 'non_field_errors') : undefined

  return (
    <div className="mx-auto max-w-sm">
      <h1 className="mb-6 text-2xl font-semibold tracking-tight">{fr.auth.register.heading}</h1>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="invite">{fr.auth.inviteCodeLabel}</Label>
          <Input
            id="invite"
            required
            value={inviteCode}
            onChange={(event) => setInviteCode(event.target.value)}
          />
          {register.isError && fieldError(register.error, 'invite_code') && (
            <p className="text-sm text-red-700 dark:text-red-400">
              {fieldError(register.error, 'invite_code')}
            </p>
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="email">{fr.auth.emailLabel}</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          {register.isError && fieldError(register.error, 'email') && (
            <p className="text-sm text-red-700 dark:text-red-400">
              {fieldError(register.error, 'email')}
            </p>
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="display_name">{fr.auth.displayNameLabel}</Label>
          <Input
            id="display_name"
            autoComplete="nickname"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
          />
          <p className="text-xs text-stone-500">{fr.auth.displayNameHint}</p>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="password">{fr.auth.passwordLabel}</Label>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          {register.isError && fieldError(register.error, 'password') && (
            <p className="text-sm text-red-700 dark:text-red-400">
              {fieldError(register.error, 'password')}
            </p>
          )}
        </div>

        {nonFieldError && <p className="text-sm text-red-700 dark:text-red-400">{nonFieldError}</p>}

        <Button type="submit" disabled={register.isPending} className="mt-2">
          {register.isPending ? fr.auth.register.submitting : fr.auth.register.submit}
        </Button>
      </form>

      <p className="mt-6 text-sm text-stone-600 dark:text-stone-400">
        {fr.auth.register.hasAccount}{' '}
        <Link to="/login" className="font-medium text-stone-900 underline dark:text-stone-100">
          {fr.auth.register.loginLink}
        </Link>
      </p>
    </div>
  )
}
